import { useState } from 'react';
import Modal from '../Modal';
import WalletButton from '../WalletButton';
import { WALLET_TYPE } from '../WalletButton';
import { Connector, useConnect, useNetwork, useSwitchNetwork } from 'wagmi';
import { CHAIN_ID } from '../../config';
import classes from './WalletConnectModal.module.css';

const WalletConnectModal: React.FC<{ onDismiss: () => void }> = props => {
  const { onDismiss } = props;
  const { connectAsync, connectors, isLoading: isConnecting } = useConnect();
  const { chain } = useNetwork();
  const { switchNetworkAsync, isLoading: isSwitching } = useSwitchNetwork();
  const [error, setError] = useState<string | null>(null);
  const [connectingWallet, setConnectingWallet] = useState<string | null>(null);

  // Find connectors by ID
  const injectedConnector = connectors.find(c => c.id === 'injected');
  const walletConnectConnector = connectors.find(c => c.id === 'walletConnect');
  const coinbaseConnector = connectors.find(c => c.id === 'coinbaseWallet');

  const isWrongNetwork = chain && chain.id !== CHAIN_ID;
  const targetNetworkName = CHAIN_ID === 1 ? 'Ethereum Mainnet' : 'Local Network';

  const handleConnect = async (connector: Connector, walletName: string) => {
    setError(null);
    setConnectingWallet(walletName);
    try {
      const result = await connectAsync({ connector });
      // Check if connected to wrong network and switch if needed
      if (result.chain.id !== CHAIN_ID && switchNetworkAsync) {
        await switchNetworkAsync(CHAIN_ID);
      }
      onDismiss();
    } catch (err: any) {
      // Don't show error if user rejected the request
      if (err.code !== 4001 && !err.message?.includes('User rejected')) {
        setError(err.message || 'Failed to connect wallet');
      }
    } finally {
      setConnectingWallet(null);
    }
  };

  const handleSwitchNetwork = async () => {
    setError(null);
    if (switchNetworkAsync) {
      try {
        await switchNetworkAsync(CHAIN_ID);
        onDismiss();
      } catch (err: any) {
        setError(err.message || 'Failed to switch network');
      }
    }
  };

  const isLoading = isConnecting || isSwitching;

  // Show network switch UI if connected but on wrong network
  if (isWrongNetwork) {
    const networkSwitchContent = (
      <div className={classes.networkSwitch}>
        <p className={classes.warningText}>You're connected to the wrong network.</p>
        <p className={classes.infoText}>
          Please switch to <strong>{targetNetworkName}</strong> to continue.
        </p>
        {error && <p className={classes.errorText}>{error}</p>}
        <button
          className={classes.switchButton}
          onClick={handleSwitchNetwork}
          disabled={isLoading}
        >
          {isSwitching ? 'Switching...' : `Switch to ${targetNetworkName}`}
        </button>
      </div>
    );
    return <Modal title="Wrong Network" content={networkSwitchContent} onDismiss={onDismiss} />;
  }

  const wallets = (
    <div className={classes.walletList}>
      {error && <p className={classes.errorText}>{error}</p>}

      {/* Browser Extension Wallets - all use injected connector */}
      {injectedConnector && (
        <>
          <WalletButton
            onClick={() => handleConnect(injectedConnector, 'metamask')}
            walletType={WALLET_TYPE.metamask}
            disabled={isLoading}
            loading={connectingWallet === 'metamask'}
          />
          <WalletButton
            onClick={() => handleConnect(injectedConnector, 'rabby')}
            walletType={WALLET_TYPE.rabby}
            disabled={isLoading}
            loading={connectingWallet === 'rabby'}
          />
          <WalletButton
            onClick={() => handleConnect(injectedConnector, 'phantom')}
            walletType={WALLET_TYPE.phantom}
            disabled={isLoading}
            loading={connectingWallet === 'phantom'}
          />
        </>
      )}

      {/* WalletConnect - for mobile wallets */}
      {walletConnectConnector && (
        <WalletButton
          onClick={() => handleConnect(walletConnectConnector, 'walletconnect')}
          walletType={WALLET_TYPE.walletconnect}
          disabled={isLoading}
          loading={connectingWallet === 'walletconnect'}
        />
      )}

      {/* Coinbase Wallet */}
      {coinbaseConnector && (
        <WalletButton
          onClick={() => handleConnect(coinbaseConnector, 'coinbase')}
          walletType={WALLET_TYPE.coinbaseWallet}
          disabled={isLoading}
          loading={connectingWallet === 'coinbase'}
        />
      )}

      <div className={classes.divider}>
        <span>or</span>
      </div>

      <p className={classes.helpText}>
        New to Ethereum?{' '}
        <a
          href="https://ethereum.org/wallets"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
        >
          Learn about wallets
        </a>
      </p>
    </div>
  );

  return <Modal title="Connect your wallet" content={wallets} onDismiss={onDismiss} />;
};
export default WalletConnectModal;
